import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import 'formdata-polyfill';
import moment from 'moment';
import { Observable, throwError } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { AnsweredChallenge, ChallengeResponse, UploadPolicy } from '../models';
import { SecureUploaderClientConfig } from '../secure-uploader-client.config';

moment.locale('en-us');

const EXPIRED_ERROR = new Error(
  'This link is expired. Please contact Ygrene Customer Support at (866) 634-1358.'
);
const INVALID_ERROR = new Error('Invalid Upload Link');
const UNKNOWN_ERROR = new Error('Unknown Error');

interface CustomeHeaders {
  [key: string]: string;
}

@Injectable()
export class SecureUploaderService {
  defaultHeaders = {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  };

  constructor(private config: SecureUploaderClientConfig, private http: HttpClient) {
    if (config.extraHeaders) {
      this.defaultHeaders = { ...this.defaultHeaders, ...config.extraHeaders };
    }
  }

  getPolicy(token: string, answers: AnsweredChallenge = {}): Observable<UploadPolicy> {
    return <Observable<UploadPolicy>>this.http
      .post(
        `${this.config.secureUploaderServiceUrl}/upload_policy`,
        { answers },
        {
          params: { token: token },
          headers: this.defaultHeaders,
        }
      )
      .pipe(
        catchError((req: HttpErrorResponse) => {
          if (req.error) {
            if (req.error.errors) {
              const an = req.error.errors;
              if (an) {
                const fullMsg = Object.keys(an).reduce((outer, k) => {
                  return `${outer} ${an[k].join(' ')}`;
                }, '');
                return throwError(new Error(fullMsg));
              }
            }
            if (req.error.msg) {
              return throwError(new Error(req.error.msg));
            }
          }
          return throwError(UNKNOWN_ERROR);
          // TODO: Make type changes to fix below error
          // Argument of type 'OperatorFunction<Object, Object>' is not assignable to parameter of
          // type 'OperatorFunction<Object, UploadPolicy>'.
        }) as any,
        tap((resp: UploadPolicy) => {
          this.validateExpires(resp.post_policy.Expires);
        })
      );
  }

  getQuestions(token: string): Observable<ChallengeResponse> {
    if (token === 'expired') {
      return throwError(EXPIRED_ERROR);
    }

    return this.http
      .get<ChallengeResponse>(`${this.config.secureUploaderServiceUrl}/upload_policy/challenge`, {
        params: { token: token },
        headers: this.defaultHeaders,
      })
      .pipe(
        catchError((err: HttpErrorResponse) => {
          return throwError(INVALID_ERROR);
        }),
        tap((resp: ChallengeResponse) => {
          this.validateExpires(resp.expires);
        })
      );
  }

  uploadFile(url: string, ygPolicy: any, file: File, customHeaders?: CustomeHeaders) {
    const toPost = Object.assign({}, ygPolicy, {
      file,
    });
    const form = new FormData();
    form.set('Content-Type', `${file.type}`);
    Object.keys(toPost).forEach((k) => {
      form.set(k, toPost[k]);
    });
    const key = `${form.get('key')}${this.randomFileName(this.stripBadChars(file.name))}`;
    form.set('key', key);
    return this.http.post(url, form, {
      observe: 'events',
      reportProgress: true,
      headers: {
        ...(customHeaders ? customHeaders : {}),
        ...(this.config.extraHeaders || {}),
      },
    });
  }

  private randomFileName(filename: string) {
    const extIndex = filename.lastIndexOf('.');
    return `${filename.slice(0, extIndex)}_${Math.random()
      .toString(36)
      .substr(2, 9)}${filename.slice(extIndex)}`;
  }

  private stripBadChars(str: string) {
    return str.replace(
      /(.*)(\.[a-zA-Z]+$)/i,
      (string, p1, p2) => `${p1.replace(/[^a-zA-Z0-9-]/gi, '')}${p2}`
    );
  }

  private validateExpires(expires: string) {
    const exp = moment.utc(expires);
    if (exp.isSameOrBefore(moment.utc())) {
      throw EXPIRED_ERROR;
    }
  }
}
